import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getOrders, getArchivedOrders, getCustomers, getAssignableUsers, deleteOrder, archiveOrder, downloadOrdersOverviewPdf } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '../components/ui/dropdown-menu';
import { StatusBadge } from '../components/StatusBadge';
import { formatOrderNo, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { Plus, Search, Filter, ClipboardList, Trash2, Eye, RefreshCw, Archive, FolderArchive, FileDown, FileText, Calendar } from 'lucide-react';

export const OrdersPage = () => {
    const [orders, setOrders] = useState([]);
    const [archivedOrders, setArchivedOrders] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [employeeFilter, setEmployeeFilter] = useState('all');
    const [customerFilter, setCustomerFilter] = useState('all');
    const [viewMode, setViewMode] = useState('active'); // 'active' or 'archived'
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [exportFilters, setExportFilters] = useState({
        filterType: 'all',
        customerId: 'all',
        employeeId: 'all',
        dateFrom: '',
        dateTo: ''
    });
    const navigate = useNavigate();

    const handleExportPdf = async () => {
        setExporting(true);
        try {
            const response = await downloadOrdersOverviewPdf(
                exportFilters.filterType,
                exportFilters.customerId !== 'all' ? exportFilters.customerId : null,
                exportFilters.employeeId !== 'all' ? exportFilters.employeeId : null,
                exportFilters.dateFrom || null,
                exportFilters.dateTo || null
            );
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const filterLabels = {
                'all': 'Alle',
                'planned': 'Geplant',
                'completed': 'Erledigt',
                'draft': 'Entwuerfe',
                'assigned': 'Zugewiesen',
                'in_progress': 'InBearbeitung'
            };
            const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
            a.download = `Auftraege_${filterLabels[exportFilters.filterType] || exportFilters.filterType}_${date}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success('PDF erstellt');
            setExportDialogOpen(false);
        } catch (err) {
            toast.error('Fehler beim Erstellen der PDF');
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = async () => {
        try {
            const [ordersRes, archivedRes, customersRes, employeesRes] = await Promise.all([
                getOrders(),
                getArchivedOrders(),
                getCustomers(),
                getAssignableUsers()
            ]);
            setOrders(ordersRes.data);
            setArchivedOrders(archivedRes.data);
            setCustomers(customersRes.data);
            setEmployees(employeesRes.data);
        } catch (err) {
            toast.error('Fehler beim Laden der Daten');
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        if (refreshing) return;
        setRefreshing(true);
        try {
            await loadData();
            toast.success('Aktualisiert');
        } catch (err) {
            toast.error('Fehler beim Aktualisieren');
        } finally {
            setRefreshing(false);
        }
    };

    const handleDelete = async () => {
        try {
            await deleteOrder(selectedOrder.id);
            toast.success('Auftrag gelöscht');
            setDeleteDialogOpen(false);
            loadData();
        } catch (err) {
            toast.error('Fehler beim Löschen');
        }
    };

    const handleArchive = async () => {
        try {
            await archiveOrder(selectedOrder.id);
            toast.success('Auftrag archiviert');
            setArchiveDialogOpen(false);
            
            // Optimistic update - move order from active to archived without full reload
            const archivedOrder = { ...selectedOrder, archived: true, archived_at: new Date().toISOString() };
            setOrders(prev => prev.filter(o => o.id !== selectedOrder.id));
            setArchivedOrders(prev => [archivedOrder, ...prev]);
            setSelectedOrder(null);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Archivieren');
        }
    };

    const currentOrders = viewMode === 'active' ? orders : archivedOrders;

    const filteredOrders = currentOrders.filter(order => {
        const matchesSearch = order.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            formatOrderNo(order.order_no).toLowerCase().includes(searchQuery.toLowerCase()) ||
            order.customer?.name?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
        const matchesEmployee = employeeFilter === 'all' || order.assigned_to_user_id === employeeFilter;
        const matchesCustomer = customerFilter === 'all' || order.customer_id === customerFilter;
        return matchesSearch && matchesStatus && matchesEmployee && matchesCustomer;
    });

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center min-h-screen">
                <div className="spinner" />
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8 space-y-6" data-testid="orders-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
                        Aufträge
                        <button 
                            onClick={handleRefresh}
                            disabled={refreshing}
                            className="p-2 rounded-full hover:bg-muted transition-colors"
                            title="Aktualisieren"
                        >
                            <RefreshCw size={20} className={`text-muted-foreground ${refreshing ? 'animate-spin' : ''}`} />
                        </button>
                    </h1>
                    <p className="text-muted-foreground">
                        {viewMode === 'active' 
                            ? `${orders.length} Aufträge` 
                            : `${archivedOrders.length} archivierte Aufträge`}
                    </p>
                </div>
                <Link to="/orders/new">
                    <Button className="btn-scale" data-testid="new-order-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Neuer Auftrag
                    </Button>
                </Link>
                <Button 
                    variant="outline" 
                    onClick={() => setExportDialogOpen(true)}
                    data-testid="export-pdf-btn"
                >
                    <FileDown className="h-4 w-4 mr-2" />
                    PDF Export
                </Button>
            </div>

            {/* View Mode Tabs */}
            <div className="flex gap-2 border-b">
                <button
                    onClick={() => setViewMode('active')}
                    className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 -mb-px ${
                        viewMode === 'active'
                            ? 'border-primary text-primary'
                            : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid="active-orders-tab"
                >
                    <ClipboardList className="h-4 w-4 inline-block mr-2" />
                    Alle Aufträge ({orders.length})
                </button>
                <button
                    onClick={() => setViewMode('archived')}
                    className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 -mb-px ${
                        viewMode === 'archived'
                            ? 'border-primary text-primary'
                            : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid="archived-orders-tab"
                >
                    <FolderArchive className="h-4 w-4 inline-block mr-2" />
                    Archiv ({archivedOrders.length})
                </button>
            </div>

            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Aufträge suchen..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 input-focus"
                        data-testid="search-input"
                    />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full md:w-[180px]" data-testid="status-filter">
                        <Filter className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Alle Status</SelectItem>
                        <SelectItem value="DRAFT">Erfasst</SelectItem>
                        <SelectItem value="ASSIGNED">Geplant</SelectItem>
                        <SelectItem value="IN_PROGRESS">In Bearbeitung</SelectItem>
                        <SelectItem value="DONE">Erledigt</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
                    <SelectTrigger className="w-full md:w-[180px]" data-testid="employee-filter">
                        <SelectValue placeholder="Mitarbeiter" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Alle Mitarbeiter</SelectItem>
                        {employees.map(emp => (
                            <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={customerFilter} onValueChange={setCustomerFilter}>
                    <SelectTrigger className="w-full md:w-[180px]" data-testid="customer-filter">
                        <SelectValue placeholder="Kunde" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Alle Kunden</SelectItem>
                        {customers.map(cust => (
                            <SelectItem key={cust.id} value={cust.id}>{cust.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Orders List */}
            {filteredOrders.length === 0 ? (
                <Card>
                    <CardContent className="empty-state py-16">
                        <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
                        <p className="text-lg font-medium">Keine Aufträge gefunden</p>
                        <p className="text-muted-foreground">Erstellen Sie Ihren ersten Auftrag</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {filteredOrders.map((order) => (
                        <Card 
                            key={order.id} 
                            className="card-hover cursor-pointer animate-fade-in"
                            onClick={() => navigate(`/orders/${order.id}`)}
                            data-testid={`order-card-${order.order_no}`}
                        >
                            <CardContent className="p-4">
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <span className="order-no text-primary">{formatOrderNo(order.order_no)}</span>
                                            <StatusBadge status={order.status} />
                                        </div>
                                        <p className="font-semibold text-lg">{order.title}</p>
                                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
                                            <span>{order.customer?.name}</span>
                                            {order.assigned_user && (
                                                <span>→ {order.assigned_user.name}</span>
                                            )}
                                            <span>Erstellt: {formatDate(order.created_at)}</span>
                                            {order.planned_date && (
                                                <span className="text-primary font-medium">Geplant: {formatDate(order.planned_date)}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => navigate(`/orders/${order.id}`)}
                                            data-testid={`view-order-${order.order_no}`}
                                        >
                                            <Eye className="h-4 w-4" />
                                        </Button>
                                        {/* Archive button - only for DONE orders in active view */}
                                        {viewMode === 'active' && order.status === 'DONE' && (
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                onClick={() => { setSelectedOrder(order); setArchiveDialogOpen(true); }}
                                                title="Archivieren"
                                                data-testid={`archive-order-${order.order_no}`}
                                            >
                                                <Archive className="h-4 w-4 text-amber-600" />
                                            </Button>
                                        )}
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => { setSelectedOrder(order); setDeleteDialogOpen(true); }}
                                            data-testid={`delete-order-${order.order_no}`}
                                        >
                                            <Trash2 className="h-4 w-4 text-destructive" />
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Delete Confirmation */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent data-testid="delete-order-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Auftrag löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Möchten Sie den Auftrag "{selectedOrder?.title}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-order-btn">
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Archive Confirmation */}
            <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
                <AlertDialogContent data-testid="archive-order-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Auftrag archivieren?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Möchten Sie den Auftrag "{selectedOrder?.title}" archivieren? Der Auftrag wird ins Archiv verschoben und kann nicht wiederhergestellt werden.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={handleArchive} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-archive-order-btn">
                            <Archive className="h-4 w-4 mr-2" />
                            Archivieren
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Export PDF Dialog */}
            <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileDown className="h-5 w-5" />
                            PDF Export
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label>Status</Label>
                            <Select 
                                value={exportFilters.filterType} 
                                onValueChange={(v) => setExportFilters({...exportFilters, filterType: v})}
                            >
                                <SelectTrigger className="mt-1">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Alle Aufträge</SelectItem>
                                    <SelectItem value="draft">Erfasst (Entwürfe)</SelectItem>
                                    <SelectItem value="assigned">Zugewiesen</SelectItem>
                                    <SelectItem value="in_progress">In Bearbeitung</SelectItem>
                                    <SelectItem value="planned">Geplant (Zugewiesen + In Bearbeitung)</SelectItem>
                                    <SelectItem value="completed">Erledigt</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label>Kunde</Label>
                            <Select 
                                value={exportFilters.customerId} 
                                onValueChange={(v) => setExportFilters({...exportFilters, customerId: v})}
                            >
                                <SelectTrigger className="mt-1">
                                    <SelectValue placeholder="Alle Kunden" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Alle Kunden</SelectItem>
                                    {customers.map(c => (
                                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label>Mitarbeiter</Label>
                            <Select 
                                value={exportFilters.employeeId} 
                                onValueChange={(v) => setExportFilters({...exportFilters, employeeId: v})}
                            >
                                <SelectTrigger className="mt-1">
                                    <SelectValue placeholder="Alle Mitarbeiter" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Alle Mitarbeiter</SelectItem>
                                    {employees.map(e => (
                                        <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="flex items-center gap-1">
                                    <Calendar className="h-4 w-4" />
                                    Datum von
                                </Label>
                                <Input 
                                    type="date"
                                    value={exportFilters.dateFrom}
                                    onChange={(e) => setExportFilters({...exportFilters, dateFrom: e.target.value})}
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label className="flex items-center gap-1">
                                    <Calendar className="h-4 w-4" />
                                    Datum bis
                                </Label>
                                <Input 
                                    type="date"
                                    value={exportFilters.dateTo}
                                    onChange={(e) => setExportFilters({...exportFilters, dateTo: e.target.value})}
                                    className="mt-1"
                                />
                            </div>
                        </div>

                        <Button 
                            variant="link" 
                            className="p-0 h-auto text-sm"
                            onClick={() => setExportFilters({
                                filterType: 'all',
                                customerId: 'all',
                                employeeId: 'all',
                                dateFrom: '',
                                dateTo: ''
                            })}
                        >
                            Filter zurücksetzen
                        </Button>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setExportDialogOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleExportPdf} disabled={exporting}>
                            <FileDown className="h-4 w-4 mr-2" />
                            {exporting ? 'Erstelle...' : 'PDF erstellen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default OrdersPage;
